import { NO_NAVIGATION, type ActiveGroup, type GroupInfo, type GroupNavigationController, type StepGuard, type StepGuardOptions } from './api/groupNavigation';
import { VERSION, FRONTEND_VERSION_TAG } from './version';
import type { InstanceApi, InstanceEventListeners } from './api/instance';
import { createPendingInstanceApi, createInstanceEventListeners } from './api/instance';
import type { WebshopConfig } from './api/webshopConfig';
import { setModalAdapter, type ModalAdapter } from './api/modalAdapter';
import { registerRenderer, type CharacteristicRenderer } from './api/renderers';
import { loadAssetPayload, ConfigentoCloudError, type LoadAssetOpts } from './api/cloudAsset';
import type { AssetPayload } from './api/assetPayload';

/**
 * A single mounted configurator. Plugin authors (Shopify, Magento,
 * standalone hosts) look up an instance via {@link ConfiguratorPublicApi.instances}
 * and drive it through the {@link InstanceApi} methods. The API methods
 * start as throwing stubs and get hot-swapped on the same object once the
 * Component finishes loading — that way a plugin can hold a reference
 * across the load-boundary without re-querying the map.
 */
export interface ConfiguratorInstance extends InstanceApi {
    readonly id: string;
    readonly componentId: string | null;
    readonly element: HTMLElement;
    /**
     * Mount-time configuration (currency, tax rate, cloud auth, host
     * locale, …) resolved from the mount element + preview context.
     * Frozen — host code that needs to change config must re-mount.
     */
    readonly config: WebshopConfig | null;
    /**
     * Replace the shop product's attribute values (`productattribute`
     * Characteristics) without re-mounting. For hosts whose product page
     * switches variants in place — a different size can mean a different
     * width or weight for the formulas. Selections stay; formulas, rules and
     * the price re-run with the new values. Same shape as `productAttributes`
     * in the mount config. Callable before the Component has loaded.
     */
    setProductAttributes(attributes: Readonly<Record<string, string>>): void;
    /**
     * Ask the visitor for a language again — the flags of the language gate
     * (`data-configento-language-select`) come back and the configurator
     * restarts in the new pick. For a "change language" button on the host
     * page. Selections stay. Does nothing where the gate would not show: no
     * `languageSelect` on the embed, or a Component with one language.
     */
    showLanguageSelect(): void;
    /** All groups currently shown (rule-hidden groups are left out), in order. */
    getGroups(): GroupInfo[];
    /** The group on screen in tabs / wizard layouts; null for `no_groups` or a single group. */
    getActiveGroup(): ActiveGroup | null;
    /**
     * Show another group, addressed by numeric id or internal name. Going
     * forward validates every step on the way (`validate: false` skips that)
     * and asks the step guard; resolves false when the step did not change.
     */
    setActiveGroup(ref: number | string, opts?: { validate?: boolean }): Promise<boolean>;
    /** Same as the built-in Next button: validate, ask the guard, advance. */
    nextGroup(): Promise<boolean>;
    /** Same as the built-in Back button. */
    prevGroup(): Promise<boolean>;
    /** Install (or remove with null) a check that can veto a step change. */
    setStepGuard(guard: StepGuard | null, opts?: StepGuardOptions): void;
}

export interface ConfiguratorPublicApi {
    readonly version: string;
    readonly frontendVersionTag: string;
    readonly instances: ReadonlyMap<string, ConfiguratorInstance>;
    readonly ready: Promise<void>;
    /**
     * Install (or clear with `undefined`) a host-side modal adapter. The
     * default bundle modal works for the admin-iframe preview; in a
     * storefront context the plugin registers an adapter that wraps the
     * shop's own modal system (Magento `Magento_Ui/js/modal/modal`,
     * Shopware bootstrap, …) so confirmations and overlays match the
     * rest of the storefront theme. See {@link ModalAdapter}.
     *
     * Plugins MUST call this before the bundle finishes loading — the
     * `<script src="…configento.js" defer>` pattern in the host page
     * guarantees the inline call runs first.
     */
    setModalAdapter(adapter?: ModalAdapter): void;

    /**
     * Draw Characteristics carrying the CSS class `name` with host code
     * instead of the built-in input. Returns an unregister function. See
     * {@link CharacteristicRenderer} and v3/docs/v3-custom-renderer-spec.md.
     */
    registerRenderer(name: string, renderer: CharacteristicRenderer): () => void;

    /**
     * Look up a mounted configurator by a DOM element OR a CSS selector.
     * Walks `el.closest('[data-configento]')` so the host can pass any
     * element inside (or equal to) the mount point — e.g. the wrapping
     * `<form id="product_addtocart_form">` on Magento storefronts. Returns
     * `null` when no mount matches.
     */
    forElement(el: HTMLElement | string): ConfiguratorInstance | null;

    /**
     * Convenience for single-configurator pages (saved-configuration mail flow,
     * standalone embeds): returns the first registered instance, or `null`
     * when none are mounted. Equivalent to `[...instances.values()][0] ?? null`,
     * spelled clearly.
     */
    first(): ConfiguratorInstance | null;

    /**
     * GET a previously stored configuration payload by id. Bypasses the
     * broken legacy `getasset.php` — relies on a deterministic filename
     * convention so the URL can be computed client-side from `id` plus
     * the active instance's `customer` / `componentId`.
     *
     * By default uses the first mounted instance; pass `opts.instance` to
     * pick a specific one when the page hosts several configurators.
     * Throws when no suitable instance is available or when the asset
     * doesn't exist.
     *
     * Caller decides whether to `inst.restoreFromAssetJson(payload)` —
     * `loadAsset` is a pure read.
     */
    loadAsset(id: string, opts?: { instance?: ConfiguratorInstance } & LoadAssetOpts): Promise<AssetPayload>;

    /**
     * Constructor for the typed error thrown by `storeAsset()` and
     * `loadAsset()`. Exposed so host code can do
     * `err instanceof Configento.CloudError` and discriminate on
     * `err.code` (a {@link ConfigentoCloudErrorCode}) without resorting
     * to regex on `err.message`.
     *
     * Codes: `NO_SELECTIONS`, `CONFIG_MISSING`, `HTTP_4XX`, `HTTP_5XX`,
     * `BAD_RESPONSE`, `NETWORK`, `NOT_FOUND` — see
     * `v3/configento-v3/src/api/cloudAsset.ts` for the per-code
     * semantics. `err.retryable` derives from `code` (true for
     * `HTTP_5XX` and `NETWORK`, false otherwise).
     */
    readonly CloudError: typeof ConfigentoCloudError;
}

declare global {
    interface Window {
        Configento?: ConfiguratorPublicApi;
    }
}

const instances = new Map<string, MutableInstance>();

// `ready` resolves once EVERY registered instance has had its live API
// attached via {@link attachInstanceApi}. Plugin authors read this as
// "the per-instance methods (`getPriceBreakdown`, `on`, …) are no longer
// throwing stubs", which is exactly when the Magento price-sync, modal
// adapter, etc. can safely fire.
//
// Lifecycle:
//   1. main.tsx finds the [data-configento] mounts, calls registerInstance
//      for each (synchronously, before any component fetch). At this point
//      the methods are pending stubs.
//   2. main.tsx then calls markMountingDone() to signal "no more mounts
//      will be registered."
//   3. ConfiguratorRoot loads each Component asynchronously and calls
//      attachInstanceApi(id, api) when its instance is live.
//   4. As soon as (a) markMountingDone has run AND (b) every registered
//      instance has an attached API, `ready` resolves.
//
// If the page has zero V3 mounts, `ready` still resolves — main.tsx
// hits markMountingDone and there are no pending attachments.
let resolveReady: () => void;
const ready = new Promise<void>((resolve) => {
    resolveReady = resolve;
});
let mountingDone = false;
const attached = new Set<string>();

function maybeResolveReady(): void {
    if (!mountingDone) return;
    for (const id of instances.keys()) {
        if (!attached.has(id)) return;
    }
    resolveReady();
}

export const publicApi: ConfiguratorPublicApi = {
    version: VERSION,
    frontendVersionTag: FRONTEND_VERSION_TAG,
    instances,
    ready,
    setModalAdapter,
    registerRenderer,
    forElement,
    first,
    loadAsset,
    CloudError: ConfigentoCloudError,
};

/**
 * Internal mutable shape — same as {@link ConfiguratorInstance} except
 * the API methods are reassignable so we can attach the live ones once
 * the Component loads. The public interface re-types these as readonly.
 */
interface MutableInstance {
    id: string;
    componentId: string | null;
    element: HTMLElement;
    config: WebshopConfig | null;
    finalize: InstanceApi['finalize'];
    restoreFromAssetJson: InstanceApi['restoreFromAssetJson'];
    getPriceBreakdown: InstanceApi['getPriceBreakdown'];
    getState: InstanceApi['getState'];
    getSelections: InstanceApi['getSelections'];
    getValue: InstanceApi['getValue'];
    getAssetPayload: InstanceApi['getAssetPayload'];
    getHeaderValue: InstanceApi['getHeaderValue'];
    storeAsset: InstanceApi['storeAsset'];
    storeCombinedImage: InstanceApi['storeCombinedImage'];
    getSummary: InstanceApi['getSummary'];
    getCombinedSku: InstanceApi['getCombinedSku'];
    getCharacteristics: InstanceApi['getCharacteristics'];
    selectValue: InstanceApi['selectValue'];
    toggleValue: InstanceApi['toggleValue'];
    setText: InstanceApi['setText'];
    setQuantity: InstanceApi['setQuantity'];
    resetSelection: InstanceApi['resetSelection'];
    on: InstanceApi['on'];
    onChange: InstanceApi['onChange'];
    /** Persistent event-listener sets. Shared across every
     *  `createInstanceApi` closure so callbacks registered ONCE
     *  (e.g. compat's `inst.on('price', …)` at Configento.ready)
     *  survive re-attaches triggered by webservice values finishing
     *  after the first attach. */
    _eventListeners: InstanceEventListeners;
    setProductAttributes: (attributes: Readonly<Record<string, string>>) => void;
    /** Attribute values set via {@link setProductAttributes}; undefined until
     *  the host sets any — the mount config's values apply until then. */
    _productAttributes: Readonly<Record<string, string>> | undefined;
    /** Subscribers (the mounted ConfiguratorRoot) to attribute changes. */
    _productAttributeListeners: Set<() => void>;
    showLanguageSelect: () => void;
    /** Subscribers (the mounted ConfiguratorRoot) to {@link showLanguageSelect}. */
    _languageSelectListeners: Set<() => void>;
    getGroups: ConfiguratorInstance['getGroups'];
    getActiveGroup: ConfiguratorInstance['getActiveGroup'];
    setActiveGroup: ConfiguratorInstance['setActiveGroup'];
    nextGroup: ConfiguratorInstance['nextGroup'];
    prevGroup: ConfiguratorInstance['prevGroup'];
    setStepGuard: ConfiguratorInstance['setStepGuard'];
    /** Registered by the mounted group navigation; NO_NAVIGATION until then. */
    _navigator: GroupNavigationController;
    _stepGuard: { guard: StepGuard; backward: boolean } | null;
    /** Cleanup for the most-recently-attached closure's store
     *  subscription. Called before the next attach so we don't leak
     *  duplicate subscribers that fire with stale `externalValues`. */
    _dispose: () => void;
}

/**
 * Register a freshly-mounted instance with the public registry. The API
 * methods start as throwing stubs and get replaced by {@link attachInstanceApi}
 * once the Component data has loaded.
 */
export function registerInstance(args: {
    id: string;
    componentId: string | null;
    element: HTMLElement;
    config: WebshopConfig | null;
}): void {
    const stub = createPendingInstanceApi();
    const listeners = new Set<() => void>();
    instances.set(args.id, {
        id: args.id,
        componentId: args.componentId,
        element: args.element,
        config: args.config,
        finalize: stub.finalize,
        restoreFromAssetJson: stub.restoreFromAssetJson,
        getPriceBreakdown: stub.getPriceBreakdown,
        getState: stub.getState,
        getSelections: stub.getSelections,
        getValue: stub.getValue,
        getAssetPayload: stub.getAssetPayload,
        getHeaderValue: stub.getHeaderValue,
        storeAsset: stub.storeAsset,
        storeCombinedImage: stub.storeCombinedImage,
        getSummary: stub.getSummary,
        getCombinedSku: stub.getCombinedSku,
        getCharacteristics: stub.getCharacteristics,
        selectValue: stub.selectValue,
        toggleValue: stub.toggleValue,
        setText: stub.setText,
        setQuantity: stub.setQuantity,
        resetSelection: stub.resetSelection,
        on: stub.on,
        onChange: stub.onChange,
        setProductAttributes(attributes) {
            const inst = instances.get(args.id);
            if (!inst) return;
            // A fresh object every call: React compares by identity, and the
            // host may mutate and re-pass the same map.
            const next: Record<string, string> = {};
            for (const [code, value] of Object.entries(attributes ?? {})) {
                if (value !== undefined && value !== null) next[code] = String(value);
            }
            inst._productAttributes = next;
            for (const cb of inst._productAttributeListeners) cb();
        },
        _productAttributes: undefined,
        _productAttributeListeners: listeners,
        showLanguageSelect() {
            for (const cb of instances.get(args.id)?._languageSelectListeners ?? []) cb();
        },
        _languageSelectListeners: new Set(),
        getGroups: () => navigatorOf(args.id).getGroups(),
        getActiveGroup: () => navigatorOf(args.id).getActive(),
        setActiveGroup: (ref, opts) => navigatorOf(args.id).goTo(ref, { validate: opts?.validate ?? true }),
        nextGroup: () => navigatorOf(args.id).next(),
        prevGroup: () => navigatorOf(args.id).prev(),
        setStepGuard(guard, opts) {
            const inst = instances.get(args.id);
            if (!inst) return;
            inst._stepGuard = guard ? { guard, backward: opts?.backward === true } : null;
        },
        _navigator: NO_NAVIGATION,
        _stepGuard: null,
        _eventListeners: createInstanceEventListeners(),
        _dispose: () => { /* no-op until first attach */ },
    });
}

/**
 * Subscribe to {@link ConfiguratorInstance.setProductAttributes} for one
 * instance — the `useSyncExternalStore` contract. Internal API.
 */
export function subscribeProductAttributes(id: string, cb: () => void): () => void {
    const inst = instances.get(id);
    if (!inst) return () => { /* nothing to unsubscribe */ };
    inst._productAttributeListeners.add(cb);
    return () => { inst._productAttributeListeners.delete(cb); };
}

/** Subscribe to {@link ConfiguratorInstance.showLanguageSelect} for one instance. Internal API. */
export function subscribeLanguageSelect(id: string, cb: () => void): () => void {
    const inst = instances.get(id);
    if (!inst) return () => { /* nothing to unsubscribe */ };
    inst._languageSelectListeners.add(cb);
    return () => { inst._languageSelectListeners.delete(cb); };
}

function navigatorOf(id: string): GroupNavigationController {
    return instances.get(id)?._navigator ?? NO_NAVIGATION;
}

/** Register the mounted group navigation of an instance (null on unmount). Internal API. */
export function setGroupNavigator(id: string, controller: GroupNavigationController | null): void {
    const inst = instances.get(id);
    if (inst) inst._navigator = controller ?? NO_NAVIGATION;
}

/** The step guard the host installed for an instance, if any. Internal API. */
export function getStepGuard(id: string): { guard: StepGuard; backward: boolean } | null {
    return instances.get(id)?._stepGuard ?? null;
}

/** Attribute values the host set at runtime, or undefined. Internal API. */
export function getProductAttributeOverride(id: string): Readonly<Record<string, string>> | undefined {
    return instances.get(id)?._productAttributes;
}

/**
 * Read the persistent event-listener sets for an instance. Passed into
 * `createInstanceApi` by the ConfiguratorRoot's attach effect so every
 * closure shares the SAME sets — subscribers registered on the previous
 * closure's `on()` method still receive events emitted by the fresh
 * closure. Internal API — plugin authors use `inst.on(...)` instead.
 */
export function getInstanceEventListeners(id: string): InstanceEventListeners | undefined {
    return instances.get(id)?._eventListeners;
}

/**
 * Replace the pending-stub methods on a registered instance with the
 * live ones. Called by `ConfiguratorRoot` after fetchComponent resolves.
 */
export function attachInstanceApi(id: string, api: InstanceApi, dispose?: () => void): void {
    const inst = instances.get(id);
    if (!inst) return;
    // Dispose the previous closure's store subscription before installing
    // the new one — otherwise every re-attach (triggered when externalValues
    // resolves after the initial component fetch) leaves a dangling
    // subscriber that continues to emit prices computed with the stale
    // externalValues. Bug caused Magento's priceBox to freeze at €0 after
    // a deep-link restore whenever webservice values arrived AFTER the
    // asset payload had already been applied.
    try { inst._dispose(); }
    catch (err) { console.warn('[Configento] previous instance dispose threw:', err); }
    inst._dispose = dispose ?? (() => { /* no-op */ });

    inst.finalize = api.finalize;
    inst.restoreFromAssetJson = api.restoreFromAssetJson;
    inst.getPriceBreakdown = api.getPriceBreakdown;
    inst.getState = api.getState;
    inst.getSelections = api.getSelections;
    inst.getValue = api.getValue;
    inst.getAssetPayload = api.getAssetPayload;
    inst.getHeaderValue = api.getHeaderValue;
    inst.storeAsset = api.storeAsset;
    inst.storeCombinedImage = api.storeCombinedImage;
    inst.getSummary = api.getSummary;
    inst.getCombinedSku = api.getCombinedSku;
    inst.getCharacteristics = api.getCharacteristics;
    inst.selectValue = api.selectValue;
    inst.toggleValue = api.toggleValue;
    inst.setText = api.setText;
    inst.setQuantity = api.setQuantity;
    inst.resetSelection = api.resetSelection;
    inst.on = api.on;
    inst.onChange = api.onChange;

    attached.add(id);
    maybeResolveReady();
}

/**
 * Signal to the public API that main.tsx has finished walking the DOM —
 * no more registerInstance() calls will arrive. Combined with the
 * attached-set above, this lets `ready` resolve correctly whether there
 * are 0, 1, or N mounts on the page.
 *
 * (Legacy name `markReady` is kept as an alias for the compat bundle's
 * historical contract — it now defers to mounting-done, same effect on
 * the resolver because attached.size === instances.size at the call
 * site.)
 */
export function markMountingDone(): void {
    mountingDone = true;
    maybeResolveReady();
}

export const markReady = markMountingDone;

/**
 * Resolve `el` to a registered configurator instance. Accepts a CSS
 * selector or an HTMLElement; walks `closest('[data-configento]')` so
 * any element inside the mount (including the host shop's wrapper form)
 * resolves to its configurator. Returns null when nothing matches.
 *
 * The instance id is `element.id` per the convention in main.tsx — see
 * `main.tsx:44`. Anonymous mounts get a `configento-anon-N` fallback id,
 * which still appears in the Map and is therefore findable here.
 */
function forElement(el: HTMLElement | string): ConfiguratorInstance | null {
    if (typeof document === 'undefined') return null;
    const resolved = typeof el === 'string' ? document.querySelector<HTMLElement>(el) : el;
    if (!resolved) return null;
    const mount = resolved.closest<HTMLElement>('[data-configento]');
    if (!mount) return null;
    // Match on the element, not on `mount.id`. The map is keyed by the
    // REGISTRATION id (`configento-anon-0` when the host didn't supply one),
    // which has nothing to do with the mount's `id` attribute — so a mount
    // without an id, which is what a plain `<div data-configento>` snippet
    // produces, never matched and every lookup came back null.
    for (const inst of instances.values()) {
        if (inst.element === mount) return inst;
    }
    return null;
}

/**
 * Return the first registered instance. Useful for single-
 * configurator pages where the host doesn't know (or care about) the
 * instance id. Returns null when no instances are mounted.
 */
function first(): ConfiguratorInstance | null {
    const it = instances.values().next();
    return it.done ? null : it.value;
}

/**
 * Fetch a previously stored configuration payload by id. Uses the active
 * instance's WebshopConfig (customer + componentId + cloudBaseUrl) to
 * compute the deterministic static URL — no PHP round-trip required.
 */
async function loadAsset(
    id: string,
    opts: { instance?: ConfiguratorInstance } & LoadAssetOpts = {},
): Promise<AssetPayload> {
    const inst = opts.instance ?? first();
    if (!inst) {
        throw new ConfigentoCloudError(
            'CONFIG_MISSING',
            'loadAsset: no mounted configurator to derive customer/component from',
        );
    }
    if (!inst.config) {
        throw new ConfigentoCloudError(
            'CONFIG_MISSING',
            'loadAsset: mounted configurator has no WebshopConfig — cloud lookup not available',
        );
    }
    return loadAssetPayload(inst.config, id, { filename: opts.filename });
}
